// Customers-specific table state mappings
import { getSingleValue, getArrayValue } from '@/lib/utils/table-state-utils';

// Filter mappings for customers - maps column IDs to tRPC input format
export const customersFilterMapping = {
    // Note: Search is handled separately via the search parameter (full-text search)
    // It searches across customer ID, email, first name, last name, and IP address

    // Store filter (multiselect)
    store: (value: any) => ({
        storeIds: getArrayValue(value)
    }),

    // Currency filter (multiselect)
    currency: (value: any) => ({
        currency: getArrayValue(value)
    }),

    // Accepts marketing filter (boolean)
    acceptsMarketing: (value: any) => ({
        acceptsMarketing: value === 'true' || value === true
    }),

    // Draft filter (boolean)
    draft: (value: any) => ({
        draft: value === 'true' || value === true
    }),

    // Card Last4 search (text)
    cardLast4: (value: any) => ({
        card: { last4: getSingleValue(value) }
    }),

    // Date range filter - pass through directly like orders and payments
    createdAt: (value: any) => ({
        date: value
    }),

    // Browser filter (multiselect)
    browser: (value: any) => ({
        browsers: getArrayValue(value)
    }),

    // Operating System filter (multiselect)
    operatingSystem: (value: any) => ({
        operatingSystems: getArrayValue(value)
    }),

    // Device Type filter (multiselect)
    deviceType: (value: any) => ({
        deviceTypes: getArrayValue(value)
    }),

    // Device Model filter (multiselect)
    deviceModel: (value: any) => ({
        deviceModels: getArrayValue(value)
    }),

    // Location filter (multiselect for country codes)
    location: (value: any) => ({
        countryCodes: getArrayValue(value)
    }),

    // Last Seen filter (date range)
    lastSeen: (value: any) => ({
        lastSeen: value
    }),

    // Tags filter (multiselect)
    tags: (value: any) => ({
        tags: getArrayValue(value)
    }),
};

export const customersQueryParamsMapping = {
    store: (value: URLSearchParams) => {
        return value.get('store')?.split(',')
    },
    currency: (value: URLSearchParams) => {
        return value.get('currency')?.split(',')
    },
    acceptsMarketing: (value: URLSearchParams) => {
        return value.get('acceptsMarketing')
    },
    draft: (value: URLSearchParams) => {
        return value.get('draft')
    },
    cardLast4: (value: URLSearchParams) => {
        return value.get('cardLast4')
    },
    createdAt: (value: URLSearchParams) => {
        const res = value.get('createdAt')
        return res ? JSON.parse(res) : null
    },
    browser: (value: URLSearchParams) => {
        return value.get('browser')?.split(',')
    },
    operatingSystem: (value: URLSearchParams) => {
        return value.get('operatingSystem')?.split(',')
    },
    deviceType: (value: URLSearchParams) => {
        return value.get('deviceType')?.split(',')
    },
    deviceModel: (value: URLSearchParams) => {
        return value.get('deviceModel')?.split(',')
    },
    location: (value: URLSearchParams) => {
        return value.get('location')?.split(',')
    },
    lastSeen: (value: URLSearchParams) => {
        const res = value.get('lastSeen')
        return res ? JSON.parse(res) : null
    },
    tags: (value: URLSearchParams) => {
        return value.get('tags')?.split(',')
    },
}

// Sort field mappings for customers (frontend column ID -> backend sort field)
export const customersSortMapping = {
    id: 'id' as const,
    email: 'email' as const,
    firstName: 'firstName' as const,
    lastName: 'lastName' as const,
    createdAt: 'createdAt' as const,
    updatedAt: 'updatedAt' as const,
    store: 'store.name' as const,
    currency: 'currency' as const,
    acceptsMarketing: 'acceptsMarketing' as const,
};
